import { formatAuditFieldLabel } from '../../components/audit/audit-entry';
import { terms } from '../../language/terms';
import { isSame } from '../../utility/strings/strings-utils';
import {
  MEMBERSHIP_LABELS,
  MEMBERSHIP_ORDER,
} from '../app-members/shared/company-status-display';

/**
 * Names the fields a security audit edit entry keeps, as the security audit log shows them: on the
 * entry's line ("Phone number, Address") and in its card of before and after (D-151 rule 5,
 * amended).
 *
 * Keys are the field names the company, site and person edits and the Glass Accreditation
 * assignment changes keep (server `SecurityAuditChanges`): the model's property names as EF's change
 * tracker yields them, after each job's renames ("Owner", "ImisReference", "ImisProperties"), plus
 * the address as one field and the processor site and all-sites switch an assignment records.
 * Keeping the model's names in the stored row and the words here means a label can change without
 * rewriting an entry. A word that names a domain concept comes from `terms`, so a rename reaches it.
 *
 * Every key those edits can keep must be here: `SecurityAuditFieldLabelCoverageTests` on the
 * server derives that set from the EF model and the jobs' audit policies, and fails naming any key
 * this map lacks. A key that still slips through (an entry written by a later job) falls back to
 * the key split into words.
 */
const SECURITY_AUDIT_FIELD_LABELS: Readonly<Record<string, string>> = {
  // More than one record.
  Name: 'Name',
  Active: 'Active',
  PhoneNumber: 'Phone number',
  LoginGroupId: terms.loginGroup.value,
  ImisReference: `${terms.imis.value} reference`,
  // A site.
  Description: 'Description',
  Address: 'Address',
  Owner: terms.siteOwner.value,
  Visible: 'Visible',
  // A person.
  Username: 'Username',
  EmailAddress: 'Email',
  DisplayName: 'Display name',
  Locked: 'Blocked',
  IsDeleted: 'Deleted',
  ImisProperties: `${terms.imis.value} details`,
  // A company.
  MembershipStatus: `${terms.membership.value} status`,
  UserNamePrefix: 'Username prefix',
  // A Glass Accreditation data entry or auditor assignment (D-166). The site is shown as its id
  // until the log names records (#398 C19).
  GlassProcessorSiteId: `${terms.primeGlassManufacturer.value} ${terms.loginSite.lower}`,
  AllSitesAccess: `All ${terms.loginSite.lowerPlural} access`,
  CertificateNotificationEmails: 'Notification recipients',
  NotifyFirstWarning: 'First warning',
  NotifyExpired: 'Expiry notification',
  NotifyAnnualReviewDue: 'Annual review due notification',
};

/**
 * Whether a kept field's before and after is text the SERVER wrote with tags to resolve, rather than
 * a value a person typed (#398 C10, the U50 rule applied to this log). Only the site's Owner is: the
 * site edit records it as a person reference (JobUpdateLoginSite's AuditPolicy), drawn as the
 * owner's name with their card. Every other value, a name, a description, a username, iMIS details,
 * is drawn exactly as stored, so a value typed as "{{person <id>}}" reads as those characters and
 * not as someone's card. Rows written before this change are covered too, since nothing is decoded
 * from them.
 */
export const securityAuditValueIsKept = (fieldName: string): boolean =>
  isSame(fieldName, 'Owner');

/** The label for a kept field, or the field name split into words when it has none. */
export const securityAuditFieldLabel = (fieldName: string): string =>
  SECURITY_AUDIT_FIELD_LABELS[fieldName] ?? formatAuditFieldLabel(fieldName);

/**
 * The kept fields whose value is a yes-or-no flag (the tracker keeps them as true or false). Each
 * is a checkbox or switch on its edit form, so the card reads Yes or No, as the record trails read
 * a tick.
 */
const FLAG_FIELDS: ReadonlySet<string> = new Set([
  'Active',
  'Visible',
  'Locked',
  'IsDeleted',
  'AllSitesAccess',
  'NotifyFirstWarning',
  'NotifyExpired',
  'NotifyAnnualReviewDue',
]);

/**
 * A kept value in the words the page owns (U47 review 1 F2): the server keeps what the tracker
 * holds, an enum member's name or true / false, because a stored word could never be renamed
 * (JobEditGroup's audit comment). A flag reads Yes or No; a membership status reads as its state
 * label from the state-colour registry. A value this does not recognise (an enum member written
 * before a rename, say) reads as stored rather than failing the row.
 */
export function securityAuditValueLabel(
  fieldName: string,
  value: string | undefined
): string | undefined {
  if (value === undefined || value === null) return value;
  if (FLAG_FIELDS.has(fieldName)) {
    if (isSame(value, 'true')) return 'Yes';
    if (isSame(value, 'false')) return 'No';
    return value;
  }
  if (fieldName === 'ImisProperties') return imisPropertiesLabel(value);
  if (fieldName === 'MembershipStatus') {
    const status = MEMBERSHIP_ORDER.find((s) => isSame(s, value));
    return status ? MEMBERSHIP_LABELS[status] : value;
  }
  return value;
}

/**
 * A person's iMIS details as "Key: value" pairs, one per line, in key order (the SA's ruling on U47
 * review 2 R1): the server keeps the dictionary as the JSON it was written as, faithful to what was
 * stored, and the card reads it as pairs. A value that is not a JSON object reads as its raw text,
 * so a row written in some other shape still shows what it holds.
 */
function imisPropertiesLabel(value: string): string {
  let parsed: object | string | number | boolean | null;
  try {
    parsed = JSON.parse(value) as object | string | number | boolean | null;
  } catch {
    return value;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed))
    return value;
  return Object.entries(parsed as Record<string, string | number | boolean>)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, item]) => `${key}: ${String(item)}`)
    .join('\n');
}
